import { API, APIParams, APIPath, APIResponse } from 'common/api/schema'
import { APIError, ErrorCode, getApiUrl } from 'common/api/utils'
import { forEach } from 'lodash'
import { removeUndefinedProps } from 'common/util/object'

// Token provider function - set by the auth layer (web, native, etc.)
let _getAccessToken: (() => Promise<string | null>) | null = null

export function setTokenProvider(fn: () => Promise<string | null>) {
  _getAccessToken = fn
}

export function unauthedApi<P extends APIPath>(path: P, params: APIParams<P>) {
  return baseApiCall(
    formatApiUrlWithParams(path, params),
    API[path].method,
    params,
    null
  ) as Promise<APIResponse<P>>
}

export const formatApiUrlWithParams = (
  path: APIPath,
  params: APIParams<APIPath>
) => {
  // parse any params that should part of the path (like market/:id)
  let url = getApiUrl(path)
  forEach(params, (v, k) => {
    if (url.includes(`:${k}`)) {
      url = url.replace(`:${k}`, v + '')
      delete (params as any)[k]
    }
  })
  return url
}

function appendQuery(url: string, props: Record<string, any>) {
  const [base, query] = url.split(/\?(.+)/)
  const params = new URLSearchParams(query)
  forEach(removeUndefinedProps(props ?? {}), (v, k) => {
    if (Array.isArray(v)) {
      v.forEach((item) => params.append(k + '[]', item))
    } else {
      params.set(k, v)
    }
  })
  return `${base}?${params.toString()}`
}

export async function baseApiCall(
  url: string,
  method: 'POST' | 'PUT' | 'GET',
  params: any,
  token: string | null
) {
  const actualUrl = method === 'POST' ? url : appendQuery(url, params)
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  const req = new Request(actualUrl, {
    headers,
    method: method,
    body:
      params == null || method === 'GET' ? undefined : JSON.stringify(params),
  })
  return fetch(req).then(async (resp) => {
    const json = (await resp.json()) as { [k: string]: any }
    if (!resp.ok) {
      throw new APIError(resp.status as ErrorCode, json?.message, json?.details)
    }
    return json
  })
}

/** Call API with the current Supabase session token */
export async function authedApiCall(
  url: string,
  method: 'POST' | 'PUT' | 'GET',
  params?: any
) {
  const token = _getAccessToken ? await _getAccessToken() : null
  return baseApiCall(url, method, params, token)
}
